import React, {useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import {useNavigate} from "react-router-dom";

import {CALC_ID, useLazyGetOrderSummaryByIdQuery, useOrderByIdQuery} from 'api/calc'
import {useGetCalcIdFromRoute} from 'utils/hooks'
import {useAccessDenied} from 'api/auth'
import {useCurrencies} from 'api/currencies'
import 'react-phone-input-2/lib/material.css'

import Preloader from "../../../../common/preloader";
import EditMainInfo, { ORDER_FORM } from './components/editMain'
import ModalBig from '../../../../common/modalBig';
import { Completed } from '@vedmarket/ui-kit';
import MenuButton from '../../../../common/menuButton';
import {PAYER_TYPE, ROUTES} from "../../../../../config/constants";
import NotFound from "../../../../additional/not-found";
import {useDemoMain} from 'components/cabinet/demo/steps/main/use-main';
const Main = ({handleAdd, onDraftNameChange} : CalculatorMainProps) => {

  const { t } = useTranslation()
  const currencies = useCurrencies()

  const orderId = useGetCalcIdFromRoute()
  const accessDenied = useAccessDenied()

  const { data: order, refetch, isError, isLoading } = useOrderByIdQuery(orderId, { skip: !orderId })
  const [ triggerGetOrderSummary ] = useLazyGetOrderSummaryByIdQuery();

  const [ showCompletedForm, setShowCompletedForm ] = useState<boolean | undefined>();

  const [ openEditModal, setOpenEditModal ] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (isError && !isLoading && !accessDenied) {
      navigate(orderId ? ROUTES.NOT_FOUND : `/${ROUTES.CABINET}/${ROUTES.CALCULATOR}`, { replace: true });
    }
  }, [ accessDenied, isError, isLoading, navigate, orderId ])

  useEffect(() => {
    if (order?.num) {
      setShowCompletedForm(true)
    }
  }, [ order?.num ])

  useEffect(() => {
    if (orderId) {
      localStorage.setItem(CALC_ID, orderId);
    }
  }, [ orderId ])


  const handleSend = () => {
    setShowCompletedForm(true)
    setOpenEditModal(false)

    if (orderId) {
      refetch()
      triggerGetOrderSummary(orderId)
    }
  }

  const onClose = () => {
    setShowCompletedForm(true);
    setOpenEditModal(false)
  }

  const payerType = order?.[ORDER_FORM.PAYER_TYPE] ?? PAYER_TYPE.LEGAL_ENTITY;
  const payerTypeText = payerType === PAYER_TYPE.INDIVIDUAL
    ? t('calc__text__main__calculationForIndividual')
    : t('calc__text__main__calculationForLegalEntity');

  const getCompletedInfo = () => {

    const comment = order?.[ORDER_FORM.COMMENT];
    const currency = order?.[ORDER_FORM.BASE_CURRENCY];
    const currencyName = currencies.find(item => item.code === currency)?.name;

    if (comment) {
      return [
        {
          label: t('common__text__comment'),
          value: comment
        },
        {
          label: t('common__text__currency'),
          value: currencyName ? `${currency} \u2014 ${currencyName}` : currency
        },
      ]
    }

    return [];
  }

  const shouldShowDemoForm = useDemoMain(orderId)

  if (shouldShowDemoForm) {
    return <EditMainInfo onSend={handleSend} onDraftNameChange={onDraftNameChange} />
  }

  if (showCompletedForm === undefined) {
    return <Preloader />
  }

  return <>
    {showCompletedForm && <Completed
      info={getCompletedInfo()}
      isFullRow
      onEdit={() => setOpenEditModal(true)}
      MenuComponent={MenuButton}
      caption={payerTypeText}
    />}
    {openEditModal && <ModalBig isOpen={openEditModal}
      onClose={onClose}
      title={t('calc__text__main__editMainData')}
    ><EditMainInfo onSend={handleSend} hasCancelButton onClose={onClose} /></ModalBig>}
  </>
}

export default Main
